import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Eye, XCircle } from 'lucide-react';
import { crushshyAdminApi } from '../../../services/api';
import { useToast } from '../../../hooks/use-toast';
import { useCrushShyT, crushshyLocale } from '../../../hooks/useCrushShyT';
import { useLanguage } from '../../../context/LanguageContext';
import CrushShyPageHeader from './CrushShyPageHeader';
import { crushCard, crushBtnPrimary } from './crushshyTheme';

const statusStyles = {
  pending: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  approved: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  rejected: 'bg-red-500/15 text-red-300 border-red-500/30',
};

const CrushShyVerifications = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('pending');
  const { toast } = useToast();
  const t = useCrushShyT();
  const { language } = useLanguage();

  const filterOptions = useMemo(
    () => [
      { value: 'pending', label: 'En attente' },
      { value: 'approved', label: 'Approuvées' },
      { value: 'rejected', label: 'Refusées' },
      { value: '', label: 'Toutes' },
    ],
    []
  );

  const load = async () => {
    try {
      const res = await crushshyAdminApi.getVerifications(filter ? { status: filter } : {});
      setItems(res.data.verifications || []);
    } catch {
      toast({ title: t('toastError'), variant: 'destructive' });
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const review = async (accountId, status) => {
    try {
      await crushshyAdminApi.reviewVerification(accountId, { status });
      toast({ title: status === 'approved' ? 'Profil vérifié' : 'Document refusé' });
      load();
    } catch {
      toast({ title: t('toastError'), variant: 'destructive' });
    }
  };

  const openDocument = async (accountId) => {
    try {
      const res = await crushshyAdminApi.getVerificationDocument(accountId);
      const blob = new Blob([res.data], { type: 'image/jpeg' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      toast({ title: 'Document introuvable', variant: 'destructive' });
    }
  };

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString(crushshyLocale(language));
    } catch {
      return iso || '—';
    }
  };

  return (
    <div className="space-y-6">
      <CrushShyPageHeader title="Vérifications identité" subtitle="KYC light — validation manuelle admin" />

      <div className="flex flex-wrap gap-2">
        {filterOptions.map((opt) => (
          <button
            key={opt.value || 'all'}
            type="button"
            onClick={() => setFilter(opt.value)}
            className={`px-3 py-1.5 rounded-lg text-sm border transition ${
              filter === opt.value
                ? 'bg-pink-500/20 border-pink-500/40 text-pink-200'
                : 'border-white/10 text-gray-400 hover:border-white/20'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {items.length === 0 ? (
          <p className="text-gray-500 text-sm">Aucune demande pour ce filtre.</p>
        ) : (
          items.map((item) => (
            <motion.div
              key={item.accountId}
              layout
              className={`${crushCard} p-4 flex flex-col sm:flex-row sm:items-center gap-4`}
            >
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate">{item.name || item.accountId}</p>
                <p className="text-xs text-gray-500 font-mono truncate">{item.accountId}</p>
                <p className="text-xs text-gray-400 mt-1">Envoyé : {formatDate(item.submittedAt)}</p>
              </div>
              <span
                className={`inline-flex self-start px-2.5 py-1 rounded-full text-xs border ${
                  statusStyles[item.status] || statusStyles.pending
                }`}
              >
                {item.status}
              </span>
              <div className="flex flex-wrap gap-2">
                {item.hasDocument ? (
                  <button
                    type="button"
                    onClick={() => openDocument(item.accountId)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/15 text-sm hover:bg-white/5"
                  >
                    <Eye size={16} />
                    Voir ID
                  </button>
                ) : null}
                {item.status === 'pending' ? (
                  <>
                    <button
                      type="button"
                      onClick={() => review(item.accountId, 'approved')}
                      className={`${crushBtnPrimary} inline-flex items-center gap-1.5 px-3 py-2 text-sm`}
                    >
                      <CheckCircle size={16} />
                      Approuver
                    </button>
                    <button
                      type="button"
                      onClick={() => review(item.accountId, 'rejected')}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-red-500/30 text-red-300 text-sm hover:bg-red-500/10"
                    >
                      <XCircle size={16} />
                      Refuser
                    </button>
                  </>
                ) : null}
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
};

export default CrushShyVerifications;
